/**
 * WWW Bundle Entry Point
 *
 * This file orchestrates the initialization of all WWW site functionality.
 * Individual concerns are organized into separate modules under ./setup/
 */

// =============================================================================
// CORE DEPENDENCIES
// =============================================================================

import 'jquery';
import { getJq } from './core.js';
import React from 'react';

// Styles extracted by webpack
import 'client/stylesheets/www.index.scss';

const $ = getJq();
// rails-ujs removed - Turbo handles all form/link interactions now

// =============================================================================
// SETUP MODULES
// =============================================================================

// Turbo Drive & Streams (must be first - handles navigation)
import './setup/turbo';

// Stimulus controllers (lazy loaded)
import './setup/stimulus';

// Analytics wrapper (attaches to window.Analytics)
import '../../../app/javascript/services/analytics';

// Bootstrap components (popovers, modals)
import { initPopovers, initModalLinks } from './setup/bootstrap';

// Fancybox lightbox (global initialization)
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

// CSRF token handling
import { initCsrf } from './setup/csrf';

// International phone input
import { setupIntlTelInputListeners } from './setup/intl_tel_input';

// Background video (Shaka player)
import { setupVideoListeners } from './setup/video';

// Declarative feature loader
import { setupFeatureListeners, loadQuizIfNeeded } from './setup/features';

// Application initialization
import { initApp, initWww, cookies } from './setup/app_init';

// External services (CallRail, Crisp chat)
import './include/externalScripts';

// =============================================================================
// INITIALIZATION
// =============================================================================

// Initialize CSRF tokens from globals.json
initCsrf();

// Setup event listeners for lazy-initialized features
setupIntlTelInputListeners();
setupVideoListeners();
setupFeatureListeners();

// Initialize Fancybox globally for all data-fancybox elements
Fancybox.bind('[data-fancybox]', {
  dragToClose: false,
  Carousel: {
    Toolbar: {
      display: {
        left: ['infobar'],
        middle: ['zoomIn', 'zoomOut', 'toggle1to1', 'rotateCCW', 'rotateCW', 'flipX', 'flipY', 'reset', 'toggleFS'],
        right: ['download', 'close']
      }
    }
  },
  Images: {
    zoom: true,
    protected: false
  }
});

// Initialize core app functionality (event delegation - only needs to run once)
initApp();

/**
 * Page setup function - runs on initial load and every Turbo navigation
 * All functions here must be idempotent (safe to call multiple times)
 */
function onPageLoad() {
  // Initialize WWW-specific functionality
  initWww();

  // Initialize Bootstrap popovers for new elements
  initPopovers();

  // Initialize legacy modal links for new elements
  initModalLinks();

  // Lazy load quiz if needed
  loadQuizIfNeeded();
}

// Run on initial DOMContentLoaded AND every Turbo navigation
// This is critical for Turbo compatibility - jQuery ready only fires once!
document.addEventListener('turbo:load', onPageLoad);

// =============================================================================
// EXPORTS & EVENTS
// =============================================================================

export { cookies };

// Dispatch load complete event
const wwwIndexLoaded = new CustomEvent('wwwIndexLoaded');
dispatchEvent(wwwIndexLoaded);
